/* eslint-disable func-style */
/* eslint-disable react/no-array-index-key */

import type { PrepurchaseProductPriceLabelParameters } from './PrepurchaseProductPriceLabels';

import { pickProductPricePromotion } from '../../../../common/components/atoms/ProductPrice/ProductPricePromotionPicker';
import { EcomProductPrice } from '../../../../ecommerce/components/EcomProductPrice';
import { getProductWeightPriceText } from '../../../../ecommerce/components/ProductInfoProperty/ProductInfoProperty.utils';
import { type ProductPresentationLabel } from '../../../../ecommerce/components/ProductPresentationSpecs';
import { ProductPriceLabel } from '../../../../ecommerce/components/ProductPriceLabel';
import {
    getAdjustedPercentageForHistoricalPricePromo,
    getComparativePriceForHigherThanHistoricalPrice,
} from './PrepurchaseProductPriceLabels.utils';

// oxlint-disable-next-line import/prefer-default-export
export const getPrePurchaseProductPrimaryComparativePriceLabel = ({
    product,
    priceType,
    a11yTextType,
    isUserMember,
}: Omit<PrepurchaseProductPriceLabelParameters, 'isB2B'>): ProductPresentationLabel | null => {
    // not much to do if we are missing comparative price
    if (!product.comparativePrice?.priceData) {
        return null;
    }

    const picked = pickProductPricePromotion({
        promo: product?.promotion,
        checkUserMemberContext: false,
        userIsMember: isUserMember,
    });
    switch (picked.type) {
        case 'fixed': {
            if (picked.promotion.higherThanHistoricalPrice) {
                break;
            }
            // show fixed promo as regular if promo price is higher than historical price
            const texts = getProductWeightPriceText({
                priceData: picked.promotion.comparativePriceData,
                unit: product.comparativePrice.unit,
                postfix: product.comparativePrice.postfix,
                fallbackText: product.comparativePrice.fallbackText,
                priceType,
            });

            const a11yText = [
                picked.promotion.isMemberPrice ? 'Medlemspris' : 'Erbjudande',
                texts?.a11y,
            ]
                .filter(Boolean)
                .join(' ');
            return {
                a11y: a11yText,
                render: () =>
                    !!texts?.visual && (
                        <ProductPriceLabel.MaybeMemberDiscount
                            isMemberPrice={picked.promotion.isMemberPrice}
                            currency
                            unit={
                                product.comparativePrice?.unit
                                    ? product.comparativePrice.unit
                                    : undefined
                            }
                            a11yType={a11yTextType}
                            a11yText={a11yText}
                        >
                            {texts.visual}
                        </ProductPriceLabel.MaybeMemberDiscount>
                    ),
            };
        }
        case 'percentage': {
            if (picked.promotion.higherThanHistoricalPrice) {
                break;
            }
            // if there is historical price but promo IS NOT higher than historical price, we need to check for adjustedPercantage to show percentage adjusted for historical price
            const finalPercentage = getAdjustedPercentageForHistoricalPricePromo({
                percentage: picked.promotion.percentageDiscount,
                adjustedPercentage: picked.promotion.adjustedPercentageDiscount,
                historicalPriceData: product.historicalPriceData,
            });
            const texts = getProductWeightPriceText({
                priceData: picked.promotion.comparativePriceData,
                unit: product.comparativePrice.unit,
                postfix: product.comparativePrice.postfix,
                fallbackText: product.comparativePrice.fallbackText,
                priceType,
            });

            const a11yText = [
                picked.promotion.isMemberPrice ? 'Medlemspris' : 'Erbjudande',
                `${finalPercentage}% rabatt,`,
                !picked.promotion.isMemberPrice && 'Pris',
                texts?.a11y,
            ]
                .filter(Boolean)
                .join(' ');

            return {
                a11y: a11yText,
                render: () => (
                    <ProductPriceLabel.MaybeMemberDiscount
                        isMemberPrice={picked.promotion.isMemberPrice}
                        a11yType={a11yTextType}
                        a11yText={a11yText}
                        secondPrice={
                            texts?.visual && (
                                <ProductPriceLabel.Discount
                                    currency
                                    unit={
                                        product.comparativePrice?.unit
                                            ? product.comparativePrice.unit
                                            : undefined
                                    }
                                    a11yType={a11yTextType}
                                    a11yText={undefined}
                                >
                                    {texts.visual}
                                </ProductPriceLabel.Discount>
                            )
                        }
                    >
                        {finalPercentage}%
                    </ProductPriceLabel.MaybeMemberDiscount>
                ),
            };
        }
        case 'multiBuy': {
            return {
                a11y: EcomProductPrice.getMultiBuyPromotionTexts(picked.promotion, priceType)
                    .a11yText,
                render: () => (
                    <EcomProductPrice.MultiBuyPromotion
                        promo={picked.promotion}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };
        }
        case 'edlp': {
            return {
                a11y: EcomProductPrice.getEdlpPriceTexts(picked.promotion, priceType).a11yText,
                render: () => (
                    <EcomProductPrice.Edlp
                        promo={picked.promotion}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };
        }

        default:
            break;
    }

    const finalComparativePrice = picked.promotion
        ? getComparativePriceForHigherThanHistoricalPrice({
              isUserMember,
              isPromoMemberPrice: !!picked.promotion?.isMemberPrice,
              comparativePriceData: product.comparativePrice.priceData,
              promoComparativePriceData: picked.promotion?.comparativePriceData,
          })
        : product.comparativePrice.priceData;

    const texts = getProductWeightPriceText({
        priceData: finalComparativePrice,
        unit: product.comparativePrice.unit,
        postfix: product.comparativePrice.postfix,
        fallbackText: product.comparativePrice.fallbackText,
        priceType,
    });

    if (!texts) {
        return null;
    }
    const a11yText = [
        !picked.promotion?.higherThanHistoricalPrice &&
        picked.promotion?.isMemberPrice &&
        isUserMember
            ? 'Medlemspris'
            : 'Pris',
        texts?.a11y,
    ]
        .filter(Boolean)
        .join(' ');

    return {
        a11y: a11yText,
        render: () => (
            <ProductPriceLabel.Regular
                currency
                unit={product.comparativePrice?.unit ? product.comparativePrice.unit : undefined}
                a11yType={a11yTextType}
                a11yText={a11yText}
            >
                {texts.visual}
            </ProductPriceLabel.Regular>
        ),
    };
};
